import React, { useState, useEffect } from "react";
import { sendMessage } from "../api";
import { useApp } from "../context/AppContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import { Label } from "./ui/label";
import { toast } from "sonner";
import { BarChart3, Plus, X, Loader2 } from "lucide-react";

function genId() {
  try { return crypto.randomUUID(); } catch (e) { return `opt-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`; }
}

/**
 * Create-a-poll dialog (groups/crews). The creator decides PER POLL whether
 * multiple answers are allowed and whether votes are anonymous.
 */
export function PollCreateDialog({ open, onClose, conversation, extraMeta = {} }) {
  const { user, t } = useApp();
  const [question, setQuestion] = useState("");
  const [options, setOptions] = useState(["", ""]);
  const [multi, setMulti] = useState(false);
  const [anonymous, setAnonymous] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setQuestion("");
      setOptions(["", ""]);
      setMulti(false);
      setAnonymous(false);
      setBusy(false);
    }
  }, [open]);

  const setOpt = (i, v) => setOptions((prev) => prev.map((o, idx) => (idx === i ? v : o)));
  const addOpt = () => setOptions((prev) => (prev.length >= 10 ? prev : [...prev, ""]));
  const rmOpt = (i) => setOptions((prev) => (prev.length <= 2 ? prev : prev.filter((_, idx) => idx !== i)));

  const cleanOptions = options.map((o) => o.trim()).filter(Boolean);
  const canCreate = question.trim().length > 0 && cleanOptions.length >= 2 && !busy;

  const create = async () => {
    if (!canCreate) return;
    setBusy(true);
    try {
      await sendMessage({
        conversation_id: conversation.id,
        sender_id: user.id,
        type: "poll",
        content: question.trim(),
        meta: {
          ...extraMeta,
          poll: {
            options: cleanOptions.map((text) => ({ id: genId(), text })),
            multi,
            anonymous,
            votes: {},
          },
        },
      });
      onClose();
    } catch (e) {
      toast.error(t("something_wrong"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="poll-create-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-primary" /> {t("create_poll")}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="poll-q" className="text-xs font-semibold">{t("poll_question")}</Label>
            <Input
              id="poll-q"
              data-testid="poll-question-input"
              placeholder={t("poll_question_ph")}
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              maxLength={200}
              autoFocus
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold">{t("poll_options")}</Label>
            <div className="space-y-2">
              {options.map((o, i) => (
                <div key={i} className="flex items-center gap-2">
                  <Input
                    data-testid={`poll-option-input-${i}`}
                    placeholder={`${t("option")} ${i + 1}`}
                    value={o}
                    onChange={(e) => setOpt(i, e.target.value)}
                    maxLength={100}
                  />
                  {options.length > 2 && (
                    <button
                      type="button"
                      data-testid={`poll-option-remove-${i}`}
                      onClick={() => rmOpt(i)}
                      className="h-8 w-8 flex items-center justify-center rounded-lg hover:bg-muted text-muted-foreground shrink-0"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
            {options.length < 10 && (
              <button
                type="button"
                data-testid="poll-add-option"
                onClick={addOpt}
                className="flex items-center gap-1.5 text-xs font-medium text-primary hover:underline mt-1"
              >
                <Plus className="h-3.5 w-3.5" /> {t("add_option")}
              </button>
            )}
          </div>
          <div className="space-y-2.5 pt-1 border-t">
            <label className="flex items-center gap-2.5 pt-2 cursor-pointer">
              <Checkbox data-testid="poll-multi-checkbox" checked={multi} onCheckedChange={(v) => setMulti(!!v)} />
              <span className="text-sm">{t("poll_multi")}</span>
            </label>
            <label className="flex items-center gap-2.5 cursor-pointer">
              <Checkbox data-testid="poll-anonymous-checkbox" checked={anonymous} onCheckedChange={(v) => setAnonymous(!!v)} />
              <span className="text-sm">{t("poll_anonymous")}</span>
            </label>
          </div>
          <Button data-testid="poll-create-submit" onClick={create} disabled={!canCreate} className="w-full h-11 rounded-xl">
            {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <BarChart3 className="h-4 w-4 mr-2" />}
            {t("create_poll")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
